import { atomFamily, selector, waitForAll } from 'recoil';
import { Data } from 'effect';
import type { Layout, LayoutItem } from 'react-grid-layout';
import type { Page } from 'venn-components';
import { PageType } from 'venn-utils';
import type { BlockId } from '../studio';
import { viewPages } from '../studio/configuration/print';
import { resetOnStudioReset } from '../effects/signalEffects';
import {
  blockContinuationPageCount,
  blockContinuationSlices,
  blockSourceLayoutState,
  blockSourcePageVisibleHeight,
} from './blockExpansion';
import { isContinuationLayoutId, makeContinuationLayoutId } from './continuationLayoutId';

export const continuationUserBlocksState = atomFamily<Layout, string>({
  key: 'continuationUserBlocks',
  default: [],
  effects: [resetOnStudioReset],
});

export interface ContinuationBlockLayout {
  readonly blockId: BlockId;
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

export type RenderedPage = Data.TaggedEnum<{
  SourcePage: { readonly sourceIndex: number; readonly page: Page };
  ContinuationPage: {
    readonly sourceBlockId: BlockId;
    readonly sourcePageIndex: number;
    readonly continuationIndex: number;
    readonly totalContinuations: number;
    readonly blockLayout: ContinuationBlockLayout;
    readonly sourcePageVisibleHeight: number;
    readonly sliceRowCount: number;
    readonly scrollOffsetPx: number;
    readonly sliceHeightPx: number;
    readonly sliceStartRowIndex: number;
    readonly sliceDataHeightPx: number;
    readonly userBlocks: Layout;
  };
}>;

export const RenderedPage = Data.taggedEnum<RenderedPage>();

export type SourcePageDescriptor = RenderedPage & { _tag: 'SourcePage' };
export type ContinuationPageDescriptor = RenderedPage & { _tag: 'ContinuationPage' };

// Derived render-list selector
export const renderedPagesState = selector<readonly RenderedPage[]>({
  key: 'renderedPages',
  get: ({ get }) => {
    const pages = get(viewPages);

    // Step 1: collect all user block IDs from GRID pages
    const allSourceBlockIds: BlockId[] = [];
    const ownerPageIndex = new Map<BlockId, number>(); // blockId -> viewPages index

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i]!;
      if (page.type !== PageType.GRID) continue;
      for (const layout of page.layout) {
        if (!isContinuationLayoutId(layout.i)) {
          allSourceBlockIds.push(layout.i);
          ownerPageIndex.set(layout.i, i);
        }
      }
    }

    // Step 2: read continuation counts for all source blocks
    const counts = get(waitForAll(allSourceBlockIds.map((id) => blockContinuationPageCount(id))));
    const countByBlock = new Map<BlockId, number>(allSourceBlockIds.map((id, idx) => [id, counts[idx] ?? 0]));

    // Step 3: read measurements for expanded blocks only
    const expandedIds = allSourceBlockIds.filter((id) => (countByBlock.get(id) ?? 0) > 0);

    const [sourceVisibleHeights, sourceLayouts, slicesByBlock] = get(
      waitForAll([
        waitForAll(expandedIds.map((id) => blockSourcePageVisibleHeight(id))),
        waitForAll(expandedIds.map((id) => blockSourceLayoutState(id))),
        waitForAll(expandedIds.map((id) => blockContinuationSlices(id))),
      ]),
    );

    const sourceVisibleByBlock = new Map<BlockId, number>(
      expandedIds.map((id, i) => [id, sourceVisibleHeights[i] ?? 0]),
    );
    const sourceLayoutByBlock = new Map<BlockId, LayoutItem | undefined>(
      expandedIds.map((id, i) => [id, sourceLayouts[i]]),
    );
    const slicesByBlockId = new Map(expandedIds.map((id, i) => [id, slicesByBlock[i] ?? []]));

    // Step 4: build the ordered rendered-page list
    const result: RenderedPage[] = [];

    for (let i = 0; i < pages.length; i++) {
      const page = pages[i]!;

      // Skip continuation pages already in viewPages — we derive them below.
      if (page.type === PageType.CONTINUATION) continue;

      result.push(RenderedPage.SourcePage({ sourceIndex: i, page }));

      if (page.type !== PageType.GRID) continue;

      // Emit continuation descriptors for each expanded block on this page.
      for (const layout of page.layout) {
        if (isContinuationLayoutId(layout.i)) continue;
        const blockId = layout.i;
        const totalContinuations = countByBlock.get(blockId) ?? 0;
        if (totalContinuations === 0) continue;

        const sourcePageVisibleHeight = sourceVisibleByBlock.get(blockId) ?? 0;
        const sourceLayout = sourceLayoutByBlock.get(blockId);
        const slices = slicesByBlockId.get(blockId) ?? [];

        for (let c = 0; c < totalContinuations; c++) {
          const slice = slices[c];
          const sliceRowCount = slice?.sliceRowCount ?? 1;
          const userBlocks = get(
            continuationUserBlocksState(makeContinuationLayoutId({ sourceBlockId: blockId, continuationIndex: c })),
          );

          result.push(
            RenderedPage.ContinuationPage({
              sourceBlockId: blockId,
              sourcePageIndex: i,
              continuationIndex: c,
              totalContinuations,
              blockLayout: sourceLayout
                ? { blockId, x: sourceLayout.x, y: sourceLayout.y, w: sourceLayout.w, h: sliceRowCount }
                : { blockId, x: 0, y: 0, w: 0, h: sliceRowCount },
              sourcePageVisibleHeight,
              sliceRowCount,
              scrollOffsetPx: slice?.scrollOffsetPx ?? 0,
              sliceHeightPx: slice?.sliceHeightPx ?? 0,
              sliceStartRowIndex: slice?.sliceStartRowIndex ?? 0,
              sliceDataHeightPx: slice?.sliceDataHeightPx ?? 0,
              userBlocks,
            }),
          );
        }
      }
    }

    return result;
  },
});

export const renderedPageIndexBySourceIndexState = selector<ReadonlyMap<number, number>>({
  key: 'renderedPageIndexBySourceIndex',
  get: ({ get }) => {
    const rendered = get(renderedPagesState);
    const map = new Map<number, number>();
    rendered.forEach((entry, renderedIndex) => {
      if (RenderedPage.$is('SourcePage')(entry)) {
        map.set(entry.sourceIndex, renderedIndex);
      }
    });
    return map;
  },
});
